/**
 * MUI Select for RMA warranty fee.
 *
 * Reads from the WarrantyFee entity slice (pre-loaded by ProcessReturnForm's
 * useRmaReferenceData hook). Replicates legacy grouping: Act of God / No
 * Warranty / Vandalism / Water Damage pinned first, then the rest of
 * non-Lamar entries, then Lamar entries at the bottom. Obsolete entries
 * are hidden unless they match the current value.
 */

import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { warrantyFeeSelector } from '@sophocles/entities/WarrantyFee';

const TOP_REASONS = new Set(['act of god', 'no warranty', 'vandalism', 'water damage']);

function groupFees(raw, value) {
  const all = raw
    .sortBy(e => e.get('id'))
    .toList()
    .filter(e => !e.get('obsolete', false) || value == e.get('id'));

  const isLamar = e => e.get('reason', '').toLowerCase().includes('lamar') && !e.get('obsolete', false);

  const lamar = all.filter(isLamar).sortBy(e => e.get('reason', ''));
  const nonLamar = all.filterNot(isLamar).sortBy(e => e.get('reason', ''));

  const top = nonLamar.filter(e => TOP_REASONS.has(e.get('reason', '').toLowerCase()));
  const mid = nonLamar.filterNot(e => TOP_REASONS.has(e.get('reason', '').toLowerCase()));

  return { top, mid, lamar };
}

/**
 * @param {object} props
 * @param {number|string|null} props.value
 * @param {function} props.onChange  - (newValue) => void
 * @param {string} [props.label]
 */
export default function WarrantyFeeSelect({ value, onChange, label }) {
  const { t } = useTranslation();
  const raw = useSelector(warrantyFeeSelector);
  const { top, mid, lamar } = groupFees(raw, value);
  const resolvedLabel = label ?? t('rma.warrantyFee', 'Warranty Fee');

  const renderGroup = (entries, keyPrefix) =>
    entries.toArray().map(e => (
      <MenuItem key={`${keyPrefix}-${e.get('id')}`} value={e.get('id')}>
        {e.get('reason')}
      </MenuItem>
    ));

  return (
    <FormControl fullWidth size="small">
      <InputLabel>{resolvedLabel}</InputLabel>
      <Select value={value ?? ''} label={resolvedLabel} onChange={e => onChange(e.target.value || null)}>
        <MenuItem value="">
          <em>{t('rma.none', 'None')}</em>
        </MenuItem>
        {renderGroup(top, 'top')}
        {!top.isEmpty() && !mid.isEmpty() && (
          <MenuItem disabled value="__sep1__">
            ────
          </MenuItem>
        )}
        {renderGroup(mid, 'mid')}
        {!lamar.isEmpty() && (
          <MenuItem disabled value="__sep2__">
            ────
          </MenuItem>
        )}
        {renderGroup(lamar, 'lamar')}
      </Select>
    </FormControl>
  );
}
